import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logoutUser } from '../../store/authSlice';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import { NotificationContainer } from './NotificationToast';
import { supabase } from '../../lib/supabaseClient';
import useDebounce from '../../hooks/useDebounce';
import QRCodeModal from '../../pages/asset-list/components/QRCodeModal';
import NotificationPanel from './NotificationPanel';

const Header = ({ user, onSearch, onProfileClick, collapsed, onMenuClick }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [selectedAssetForQR, setSelectedAssetForQR] = useState(null);
  
  const searchRef = useRef(null);
  const debouncedTerm = useDebounce(searchQuery, 300);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsResultsVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Supabase Omni-Search Logic
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedTerm.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      setIsResultsVisible(true);

      try {
        const trimmedTerm = debouncedTerm.trim();
        const isPic = user?.role === 'pic' || user?.role === 'department_pic';

        let query = supabase
          .from('assets')
          .select('*, suppliers(company_name), locations(name), loans(status, employees(department_id))')
          .or(`product_name.ilike.%${trimmedTerm}%,asset_tag.ilike.%${trimmedTerm}%,serial_number.ilike.%${trimmedTerm}%`);

        if (!isPic) {
          query = query.limit(5);
        }

        const { data, error } = await query;

        if (error) throw error;

        let results = data || [];
        if (isPic && user?.department_id) {
          results = results.filter(asset => 
            asset.current_department_id === user.department_id ||
            asset.loans?.some(loan => 
              (loan.status === 'active' || loan.status === 'In Use' || loan.status === 'overdue') && 
              loan.employees?.department_id === user.department_id
            )
          ).slice(0, 5);
        }
        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedTerm]);

  const addNotification = (message, type) => {
    setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (!isResultsVisible) setIsResultsVisible(true);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;

    setIsResultsVisible(false);
    
    if (searchResults.length > 0) {
      const firstResult = searchResults[0];
      // Use asset_tag if available, fallback to id
      navigate(`/assets/${firstResult.asset_tag || firstResult.id}`);
    } else {
      // Navigate to asset list with search param as requested
      navigate(`/asset-list?search=${encodeURIComponent(query)}`);
    }
    
    setSearchQuery('');
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleProfileAction = async (action) => {
    setIsProfileOpen(false);
    if (action === 'logout') {
      try {
        await dispatch(logoutUser()).unwrap();
        addNotification(t('notifications.logged_out_success'), 'success');
        setTimeout(() => navigate('/login', { replace: true }), 1000);
      } catch (error) {
        console.error("Logout failed:", error);
        addNotification(t('notifications.logout_failed'), 'error');
      }
    } else if (onProfileClick) {
      onProfileClick(action);
    }
  };

  return (
    <header className="w-full h-16 bg-card border-b border-border">
      <NotificationContainer notifications={notifications} onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        {/* Mobile Menu Button */}
        <div className="lg:hidden mr-2">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted/50 hover:text-gray-900 transition-colors"
          >
            <Icon name="Menu" size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div ref={searchRef} className="flex-1 max-w-2xl">
          <div className="relative">
            <div className={`relative transition-all duration-200 ${
              isSearchExpanded ? 'w-full' : 'w-full md:w-96'
            }`}>
              <Input
                type="search"
                placeholder={t('placeholders.search_assets')}
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  setIsSearchExpanded(true);
                  if (searchQuery.length >= 2) setIsResultsVisible(true);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                className="pl-9 pr-10 h-10 bg-muted border-border focus:bg-white focus:ring-2 focus:ring-ring/10 transition-all rounded-lg text-sm"
              />
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70"
              />
              {isSearching ? (
                <Icon
                  name="Loader2"
                  size={14}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 animate-spin"
                />
              ) : searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    setIsResultsVisible(false);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-muted-foreground/70 hover:text-gray-600"
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>

            {/* Search Results Dropdown */}
            {isResultsVisible && searchQuery.trim().length >= 2 && (
              <div className="absolute top-full left-0 mt-2 w-full md:w-[480px] bg-card border border-border rounded-xl shadow-xl z-[200] overflow-hidden">
                {isSearching ? (
                  <div className="p-8 text-center">
                    <div className="flex flex-col items-center space-y-2">
                      <Icon name="Loader2" size={24} className="text-blue-500 animate-spin" />
                      <span className="text-sm font-medium text-muted-foreground">{t('messages.loading_search')}</span>
                    </div>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-2">
                    <div className="px-4 py-2 text-[11px] font-bold text-muted-foreground/70 uppercase tracking-wider">
                      {t('header.quick_results')}
                    </div>
                    {searchResults.map((asset) => (
                      <div 
                        key={asset.id} 
                        className="group flex items-center justify-between px-4 py-3 hover:bg-muted cursor-pointer transition-colors"
                        onClick={() => {
                          navigate(`/assets/${asset.asset_tag || asset.id}`);
                          setIsResultsVisible(false);
                          setSearchQuery('');
                        }}
                      >
                        <div className="flex items-center space-x-3 overflow-hidden">
                          <div className="w-10 h-10 rounded-lg bg-muted/50 flex-shrink-0 flex items-center justify-center overflow-hidden border border-border">
                            {asset.image_url ? (
                              <img src={asset.image_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Icon name="Package" size={18} className="text-muted-foreground/70" />
                            )}
                          </div>
                          <div className="truncate">
                            <div className="text-sm font-bold text-foreground truncate">
                              {asset.product_name}
                            </div>
                            <div className="flex items-center space-x-2 mt-0.5">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-primary/10 text-primary rounded border border-primary/20 uppercase">
                                {asset.asset_tag}
                              </span>
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                asset.status === 'In Use' ? 'bg-green-50 text-green-700' :
                                asset.status === 'Maintenance' ? 'bg-amber-50 text-amber-700' :
                                'bg-muted/50 text-muted-foreground'
                              }`}>
                                {asset.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Icon name="ChevronRight" size={14} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
                      </div>
                    ))}
                    <div className="p-2 bg-muted border-t border-border">
                      <button 
                        className="w-full py-2 text-xs text-center text-primary hover:text-blue-700 font-bold transition-colors"
                        onClick={() => {
                          const query = searchQuery.trim();
                          navigate(`/asset-list?search=${encodeURIComponent(query)}`);
                          setIsResultsVisible(false);
                          setSearchQuery('');
                        }}
                      >
                        {t('header.view_all_results')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-sm text-muted-foreground font-medium">{t('header.no_assets_found', { query: searchQuery })}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center space-x-1 md:space-x-3">
          {/* Notifications */}
          <NotificationPanel user={user} />

          {/* User Profile */}
          <div className="relative pl-1 md:pl-3 border-l border-border">
            <button
              onClick={toggleProfile}
              className="flex items-center space-x-2 group p-1 rounded-xl hover:bg-muted transition-all"
            >
              <div className="w-8 h-8 bg-primary text-white rounded-lg flex items-center justify-center text-xs font-bold shadow-md shadow-blue-500/20 overflow-hidden">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  user?.full_name?.charAt(0) || 'U'
                )}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-[12px] font-bold text-foreground leading-tight">
                  {user?.full_name || 'User'}
                </div>
                <div className="text-[10px] text-muted-foreground font-medium">
                  {user?.role === 'system_admin' ? 'System Admin' : user?.role || 'IT Staff'}
                </div>
              </div>
              <Icon name="ChevronDown" size={14} className="text-muted-foreground/70 group-hover:text-gray-600 transition-colors" />
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-2xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-4 bg-muted/50 border-b border-border">
                  <div className="font-bold text-sm text-foreground">
                    {user?.full_name || 'User Name'}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-medium truncate mt-0.5">
                    {user?.email || 'user@panasonic.com'}
                  </div>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => handleProfileAction('profile')}
                    className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-blue-50 hover:text-blue-600 rounded-lg flex items-center space-x-3 transition-colors group"
                  >
                    <Icon name="User" size={16} className="text-muted-foreground/70 group-hover:text-blue-500" />
                    <span className="font-medium">{t('sidebar.profile_settings')}</span>
                  </button>
                  <button
                    onClick={() => handleProfileAction('preferences')}
                    className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-blue-50 hover:text-blue-600 rounded-lg flex items-center space-x-3 transition-colors group"
                  >
                    <Icon name="Settings" size={16} className="text-muted-foreground/70 group-hover:text-blue-500" />
                    <span className="font-medium">{t('sidebar.preferences')}</span>
                  </button>
                  <div className="h-px bg-muted/50 my-1 mx-2" />
                  <button
                    onClick={() => handleProfileAction('logout')}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center space-x-3 transition-colors group"
                  >
                    <Icon name="LogOut" size={16} className="text-red-400 group-hover:text-red-600" />
                    <span className="font-bold">{t('common.sign_out')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* QR Code Modal */}
      {selectedAssetForQR && (
        <QRCodeModal 
          asset={selectedAssetForQR}
          isOpen={!!selectedAssetForQR}
          onClose={() => setSelectedAssetForQR(null)}
        />
      )}
    </header>
  );
};

export default Header;